
import { formatDate } from "@/lib/utils";
import { DateRecommendation, Guest } from "./types";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

interface DateCardProps {
  recommendation: DateRecommendation;
  isSelected: boolean;
  onClick: () => void;
  guests: Guest[];
}

export const DateCard = ({ 
  recommendation, 
  isSelected, 
  onClick, 
  guests
}: DateCardProps) => {
  // Calculate how many guests can attend on this date
  const availableGuests = guests.filter(guest => 
    guest.selected_dates?.includes(recommendation.recommended_date)
  );
  
  const availableGuestsCount = availableGuests.length;
  
  const availabilityPercentage = guests.length > 0 
    ? Math.round((availableGuestsCount / guests.length) * 100) 
    : 0;
  
  // Get the first few available guests for display
  const visibleGuests = availableGuests.slice(0, 3);
  
  const formattedDate = formatDate(new Date(recommendation.recommended_date));
  
  // Use consistent initials function across the app
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };
  
  return (
    <div 
      className={`
        p-4 rounded-lg border 
        ${isSelected 
          ? 'border-primary ring-2 ring-primary ring-offset-2' 
          : 'border-border hover:border-neutral-400'
        }
        transition-all cursor-pointer
      `}
      onClick={onClick}
    >
      <div className="mb-2">
        <h3 className="text-lg font-medium">{formattedDate}</h3>
        <p className="text-sm text-gray-500">{recommendation.time_of_day}</p>
      </div>
      
      <div className="flex justify-between items-center mt-3">
        <div className="flex -space-x-2">
          {visibleGuests.map((guest, index) => (
            <Avatar key={index} className={cn(
              "w-6 h-6",
              guest.rsvp_status === 'accepted' ? "border-2 border-white bg-black text-white" : 
              guest.rsvp_status === 'declined' ? "border-2 border-black bg-gray-400 text-black" : 
              "border-2 border-black bg-white text-black"
            )}>
              {/* First try to use guest_avatar_url, then fall back to avatar_url */}
              {guest.guest_avatar_url ? (
                <AvatarImage src={guest.guest_avatar_url} alt={guest.display_name} />
              ) : guest.avatar_url ? (
                <AvatarImage src={guest.avatar_url} alt={guest.display_name} />
              ) : (
                <AvatarFallback className="text-xs">
                  {getInitials(guest.display_name)}
                </AvatarFallback>
              )}
            </Avatar>
          ))}
          
          {availableGuestsCount > 3 && (
            <Avatar className="w-6 h-6 border-2 border-black bg-white text-black">
              <AvatarFallback className="text-xs">
                +{availableGuestsCount - 3}
              </AvatarFallback>
            </Avatar>
          )}
        </div>
        
        <span className="text-sm font-medium">
          {availableGuestsCount > 0 
            ? `${availableGuestsCount} available` 
            : "No responses yet"}
        </span>
      </div>
      
      {guests.length > 0 && (
        <div className="mt-2 text-xs text-gray-500">
          {availabilityPercentage}% of guests available
        </div>
      )}
    </div>
  );
};
